/* eslint-disable no-unused-vars */
'use client';

import { removerValoresResumoCompra } from '@/components/organisms/ResumoCompra/hook/useResumoCompraHook';
import { removerValorPedidoIDComFalha } from '@/components/pages/Revisao/hook/useRevisaoPageHook';
import { useSnackbar } from '@/hooks/useSnackbar';
import { registrarEvento } from '@/services/googleAnalytics';
import {
  recuperarCombo,
  recuperarProduto,
  recuperarProdutoPeloId,
} from '@/services/marketplaceService';
import { CartaoCredito } from '@/services/pagamentos/types/pagamento';
import { Combo, ProdutoCombo } from '@/types/combo';
import { Sacola, ItemSacola } from '@/types/sacola';
import { useRouter } from 'next/navigation';
import {
  createContext,
  useState,
  ReactNode,
  useMemo,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
} from 'react';
import { useMetaTrackHook } from '@/hooks/useMetaTrackHook';

export type FormaPagamento = {
  metodo: 'pix' | 'cartao';
  cartao?: CartaoCredito;
};

type SacolaDataType = Sacola & {
  adicionarItemASacola: (
    item: ItemSacola,
    podeMostrarSnackbar?: boolean,
  ) => void;
  atualizarPlanoSacola: ({
    plano,
    opcaoSelecionadaPlano,
  }: {
    plano: Combo;
    opcaoSelecionadaPlano: ProdutoCombo;
  }) => void;
  atualizarQuantidadeItemSacola: ({
    idItem,
    planoEscolhidoID,
    quantidade,
  }: {
    idItem: string;
    planoEscolhidoID: string;
    quantidade: number;
  }) => void;
  removerItemDaSacola: (index: number) => void;
  valorCompra: number;
  formaDePagamento: FormaPagamento | null;
  informarFormaPagemento: Dispatch<SetStateAction<FormaPagamento | null>>;
  planoASerEditado: string | null;
  informarPlanoASerEditado: Dispatch<SetStateAction<string | null>>;
  recuperandoValoresSacola: boolean;
  limparSacola: () => void;
  formatarFormaPagamento: () => void;
  existeProdutoNaSacola: (itens: ItemSacola[]) => boolean;
  existeComboNaSacola: (itens: ItemSacola[]) => boolean;
  compraRecente: ItemSacola[];
  setCompraRecente: Dispatch<SetStateAction<ItemSacola[]>>;
  usuarioAceitouOsTermos: boolean;
  informarUsuarioAceitouTermos: Dispatch<SetStateAction<boolean>>;
};

export const SacolaDataContext = createContext<SacolaDataType | undefined>(
  undefined,
);

export const SacolaDataProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();
  const [itensSacola, setItensSacola] = useState<ItemSacola[]>([]);
  const [compraRecente, setCompraRecente] = useState<ItemSacola[]>([]);

  const [formaDePagamento, setFormaPagamento] = useState<FormaPagamento | null>(
    null,
  );
  const [planoASerEditado, setPlanoASerEditado] = useState<string | null>(null);
  const [recuperandoValoresSacola, setRecuperandoValoresSacola] =
    useState<boolean>(true);

  const { mostrarSnackbar } = useSnackbar();

  function salvarItensDaSacolaNoLocalStorage(itens: ItemSacola[]) {
    const ids = itens.map((item) => ({
      produtoSlug: item.produto?.slug,
      opcaoSelecionadaProdutoID: item.opcaoSelecionadaProduto?.id,
      comboSlug: item.plano?.slug,
      opcaoSelecionadaPlanoID: item.opcaoSelecionadaPlano?.id,
      quantidade: item.quantidade,
    }));
    localStorage.setItem('chippuPlace@itensSacola', JSON.stringify(ids));
  }

  async function recuperarItensDaSacolaDoLocalStorage() {
    setRecuperandoValoresSacola(true);
    const itensSalvos = localStorage.getItem('chippuPlace@itensSacola');
    if (itensSalvos) {
      try {
        const ids: Array<{
          produtoSlug: string;
          opcaoSelecionadaProdutoID: string;
          comboSlug: string;
          opcaoSelecionadaPlanoID: string;
          quantidade: number;
        }> = JSON.parse(itensSalvos);

        if (ids.length) {
          for (const itemSacolaSalvoStorage of ids) {
            if (itemSacolaSalvoStorage.produtoSlug) {
              const produtoFromServer = await recuperarProduto(
                itemSacolaSalvoStorage.produtoSlug,
              );

              const planoSelecionadoProduto = produtoFromServer.planos?.find(
                (planoProduto) =>
                  planoProduto.id ===
                  itemSacolaSalvoStorage.opcaoSelecionadaProdutoID,
              );

              if (!planoSelecionadoProduto) return;

              adicionarItemASacola(
                {
                  produto: produtoFromServer,
                  opcaoSelecionadaProduto: planoSelecionadoProduto,
                  quantidade: itemSacolaSalvoStorage.quantidade,
                },
                false,
              );
            }

            if (itemSacolaSalvoStorage.comboSlug) {
              const comboFromServer = await recuperarCombo(
                itemSacolaSalvoStorage.comboSlug,
              );

              adicionarItemASacola(
                {
                  plano: comboFromServer,
                  opcaoSelecionadaPlano:
                    comboFromServer.produtos?.find(
                      (comboProduto) =>
                        comboProduto.id ===
                        itemSacolaSalvoStorage.opcaoSelecionadaPlanoID,
                    ) ?? undefined,
                  quantidade: itemSacolaSalvoStorage.quantidade,
                },
                false,
              );
            }
          }
        }
      } catch (error) {
        console.error('Erro ao recuperar itens da sacola:', error);
      } finally {
        setTimeout(() => setRecuperandoValoresSacola(false), 500);
      }
    } else {
      setRecuperandoValoresSacola(false);
    }
  }

  const executouRecuperacaoItensUrl = useRef(false);

  const { recuperarQueryStringDaUrl } = useMetaTrackHook();

  async function recuperarItensDaSacolaDaURL() {
    setRecuperandoValoresSacola(true);

    try {
      if (typeof window === 'undefined') return;

      const urlParams = new URLSearchParams(window.location.search);

      const produtoID = urlParams.get('produto_id');
      const planoID = urlParams.get('plano_id');

      if (!produtoID || !planoID)
        throw new Error(
          'Não foi possível recuperar produto ou plano dos itens na URL',
        );

      const produtoFromServer = await recuperarProdutoPeloId(produtoID);

      if (!produtoFromServer)
        throw new Error(
          'Não foi possível recuperar o produto dos itens na URL',
        );

      const planoSelecionadoProduto = produtoFromServer.planos?.find(
        (planoProduto) => planoProduto.id === planoID,
      );

      if (!planoSelecionadoProduto)
        throw new Error('Não foi possível recuperar o plano do produto');

      adicionarItemASacola(
        {
          produto: produtoFromServer,
          opcaoSelecionadaProduto: planoSelecionadoProduto,
          quantidade: 1,
        },
        false,
      );

      router.replace(window.location.pathname);
    } catch (error) {
      console.error('Erro ao recuperar itens da sacola:', error);
    } finally {
      setRecuperandoValoresSacola(false);
    }
  }

  useEffect(() => {
    recuperarItensDaSacolaDoLocalStorage();

    if (!executouRecuperacaoItensUrl.current) {
      executouRecuperacaoItensUrl.current = true;
      recuperarQueryStringDaUrl('add_cart');
      recuperarItensDaSacolaDaURL();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    salvarItensDaSacolaNoLocalStorage(itensSacola);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itensSacola]);

  function existeProdutoNaSacola(itensSacolaParametro: ItemSacola[]) {
    return itensSacolaParametro.some(
      (itemAnterior) => itemAnterior.produto?.id !== undefined,
    );
  }

  function existeComboNaSacola(itensSacolaParametro: ItemSacola[]) {
    return itensSacolaParametro.some(
      (itemAnterior) => itemAnterior.plano?.comboID !== undefined,
    );
  }

  function adicionarItemASacola(item: ItemSacola, podeMostrarSnackbar = true) {
    removerValoresResumoCompra();
    removerValorPedidoIDComFalha();

    return setItensSacola((itensAnteriores) => {
      const existeCombo = existeComboNaSacola(itensAnteriores);

      const existeProduto = existeProdutoNaSacola(itensAnteriores);

      if ((existeCombo && item.produto) || (existeProduto && item.plano)) {
        if (podeMostrarSnackbar)
          mostrarSnackbar({
            size: 1,
            variant: 'solid',
            color: 'error',
            highContrast: false,
            mensagem:
              'Você só pode adicionar produtos avulsos ou planos à sacola por vez.',
          });
        return itensAnteriores;
      }

      if (item.plano?.comboID) {
        const planoExistente = itensAnteriores.find((itemAnterior) => {
          const comboIDAnterior = itemAnterior.plano?.comboID;
          const comboIDAtual = item.plano?.comboID;
          return comboIDAnterior === comboIDAtual;
        });

        if (planoExistente) {
          mostrarSnackbar({
            size: 1,
            variant: 'solid',
            color: 'error',
            highContrast: false,
            mensagem: 'Você já possui este item adicionado à sacola',
          });

          return itensAnteriores;
        }
      }

      if (item.produto?.id) {
        const produtoExistente = itensAnteriores.find(
          (itemAnterior) =>
            itemAnterior.produto?.id === item.produto?.id &&
            itemAnterior?.opcaoSelecionadaProduto?.id ===
              item.opcaoSelecionadaProduto?.id,
        );

        if (produtoExistente) {
          if (item.produto.assinatura) {
            if (podeMostrarSnackbar)
              mostrarSnackbar({
                size: 1,
                variant: 'solid',
                color: 'error',
                highContrast: false,
                mensagem: 'Você já possui este item adicionado à sacola',
              });

            return itensAnteriores;
          }

          return itensAnteriores.map((itemAnterior) =>
            itemAnterior.produto?.id === produtoExistente.produto?.id &&
            itemAnterior?.opcaoSelecionadaProduto?.id ===
              produtoExistente.opcaoSelecionadaProduto?.id
              ? {
                  ...itemAnterior,
                  quantidade: itemAnterior.quantidade + item.quantidade,
                }
              : itemAnterior,
          );
        }
      }

      if (podeMostrarSnackbar)
        mostrarSnackbar({
          color: 'success',
          highContrast: true,
          mensagem: 'Item adicionado à sacola',
          size: 1,
          variant: 'solid',
        });

      return [...itensAnteriores, item];
    });
  }

  function atualizarPlanoSacola({
    plano,
    opcaoSelecionadaPlano,
  }: {
    plano: Combo;
    opcaoSelecionadaPlano: ProdutoCombo;
  }) {
    removerValorPedidoIDComFalha();
    return setItensSacola((itensAnteriores) => {
      const planoExistente = itensAnteriores.find(
        (itemAnterior) => itemAnterior.plano?.comboID === plano?.comboID,
      );

      if (planoExistente) {
        return itensAnteriores.map((itemAnterior) =>
          itemAnterior.plano?.comboID === planoExistente.plano?.comboID
            ? {
                ...itemAnterior,
                opcaoSelecionadaPlano,
              }
            : itemAnterior,
        );
      }

      return itensAnteriores;
    });
  }

  function atualizarQuantidadeItemSacola({
    idItem,
    planoEscolhidoID,
    quantidade,
  }: {
    idItem: string;
    planoEscolhidoID: string;
    quantidade: number;
  }) {
    removerValorPedidoIDComFalha();
    return setItensSacola((itensAnteriores) => {
      return itensAnteriores.map((item) =>
        item?.produto?.id === idItem &&
        item?.opcaoSelecionadaProduto?.id === planoEscolhidoID
          ? { ...item, quantidade }
          : item,
      );
    });
  }

  function removerItemDaSacola(index: number) {
    removerValorPedidoIDComFalha();

    const itemSacola = { ...itensSacola[index] };
    let valor;
    let id;
    let nome;
    let desconto;
    let marca;
    if (itemSacola?.opcaoSelecionadaProduto) {
      id = itemSacola?.opcaoSelecionadaProduto?.id;
      nome = itemSacola?.opcaoSelecionadaProduto?.nome;
      desconto = itemSacola?.opcaoSelecionadaProduto?.desconto;
      valor = itemSacola?.opcaoSelecionadaProduto?.preco;
      marca = itemSacola?.produto?.nome;
    } else if (itemSacola?.plano) {
      id = itemSacola?.plano?.comboID;
      nome = itemSacola?.plano?.nome;
      desconto = itemSacola?.plano?.desconto;
      valor = itemSacola?.plano?.preco;
    }
    registrarEvento('sacola_clicou_remover_item', { item: itemSacola });
    registrarEvento('remove_from_cart', {
      currency: 'BRL',
      value: valor,
      items: [
        { item_id: id, item_brand: marca, item_name: nome, discount: desconto },
      ],
    });

    setItensSacola((itensAnteriores) => {
      return itensAnteriores.filter((_, i) => i !== index);
    });
  }

  function limparSacola() {
    removerValorPedidoIDComFalha();
    setItensSacola([]);
  }

  function formatarFormaPagamento() {
    setFormaPagamento(null);
  }

  const valorCompra = useMemo(() => {
    return itensSacola.reduce((acc, item) => {
      if (item.opcaoSelecionadaProduto) {
        return (
          acc +
          (item.opcaoSelecionadaProduto.preco -
            (item?.produto?.desconto || 0)) *
            item.quantidade
        );
      }
      const valorPlano = Number(item?.plano?.preco) ?? 0;
      return acc + valorPlano * Math.max(item.quantidade, 1);
    }, 0);
  }, [itensSacola]);

  const [usuarioAceitouOsTermos, setUsuarioAceitouOsTermos] = useState(false);

  return (
    <SacolaDataContext.Provider
      value={{
        itens: itensSacola,
        adicionarItemASacola,
        atualizarQuantidadeItemSacola,
        atualizarPlanoSacola,
        removerItemDaSacola,
        valorCompra,
        formaDePagamento,
        informarFormaPagemento: setFormaPagamento,
        planoASerEditado,
        informarPlanoASerEditado: setPlanoASerEditado,
        recuperandoValoresSacola,
        limparSacola,
        formatarFormaPagamento,
        existeProdutoNaSacola,
        existeComboNaSacola,
        compraRecente,
        setCompraRecente,
        usuarioAceitouOsTermos,
        informarUsuarioAceitouTermos: setUsuarioAceitouOsTermos,
      }}
    >
      {children}
    </SacolaDataContext.Provider>
  );
};
